import { useState } from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import Button from '@mui/material/Button'

import LoadingBox from "@/components/loading-box"
import ErrorBox from "@/components/error-box"
import { Fade } from '@mui/material'
import { useCollectionCompanies } from '@/hooks/api/use-collection-company'
import PermissionsGate from '../permissions-gate'
import { UserRole } from '@/types'
import CollectionCompanyForm from '../forms/collection-company'

function CollectionCompanyManagementList() {

    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [create, setCreate] = useState<boolean>(false)
    
    const { status, data, error } = useCollectionCompanies()

    if (status === 'pending') {
        return (
            <LoadingBox />
        )
    }

    if (error) {
        return <ErrorBox
            error={error}
            message={'Nepovedlo se načíst seznam svozových společností'}
        />
    }

    const selectedEntry = data.find(entry => entry.id === selectedId)

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            overflowX: 'auto'
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: {
                    xs: 'column',
                    xl: 'row',
                },
                pb: 2,
            }}>
                <Box sx={theme => ({
                    flexBasis: {
                        xs: '100%',
                        xl: selectedId ? '70%' : '100%',
                    },
                    transition: theme.transitions.create(['flex-basis', 'padding'], {
                        duration: 200,
                    }),
                    pr: {
                        xs: 0,
                        xl: selectedId ? 3 : 0,
                    },
                    overflowX: 'auto',
                })}>
                    {data.length === 0 ?
                        <Box px={2.5}>
                            Seznam je prázdný
                        </Box>
                    :
                        <Box>
                            <Table
                                size="small"
                                aria-label="Seznam obcí"
                                sx={{
                                    mt: 1,
                                    minWidth: 600,
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ paddingLeft: 3 }}>ID</TableCell>
                                        <TableCell>EOKO ID</TableCell>
                                        <TableCell>Svozová společnost</TableCell>
                                        <TableCell>Dny na reklamaci</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((row, index) => (
                                        <TableRow
                                            key={row.id}
                                            sx={{
                                                // cursor: 'pointer',
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                ...(row.id === selectedId ? {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                } : null),
                                            }}
                                            // onClick={() => setSelectedId(row.id)}
                                            // hover
                                        >
                                            <TableCell component="th" scope="row" sx={{ paddingLeft: 3, py: 2 }}>
                                                {row.id}
                                            </TableCell>
                                            <TableCell>
                                                {row.eoko_customer?.id}
                                            </TableCell>
                                            <TableCell>
                                                {row.eoko_customer?.name}
                                            </TableCell>
                                            <TableCell>
                                                {row.uncollected_bin_report_delay_days}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    
                                </TableBody>
                            </Table>
                            <PermissionsGate
                                userRoles={[
                                    UserRole.admin,
                                    UserRole.eokoDataAdmin,
                                ]}
                            >
                                <Box px={2.5} py={1.5}>
                                    {create ?
                                        <Box maxWidth="sm">
                                            <CollectionCompanyForm
                                                closeCallback={() => setCreate(false)}
                                            />
                                        </Box>
                                        :
                                        <Button
                                            variant="contained"
                                            color="terciary"
                                            startIcon={<AddRoundedIcon />}
                                            onClick={() => setCreate(true)}
                                        >
                                            Přidat novou svozovou společnost
                                        </Button>
                                    }
                                </Box>
                            </PermissionsGate>
                        </Box>
                    }
                </Box>
                <Box sx={(theme) => ({
                    flexBasis: {
                        xs: '100%',
                        xl: selectedId ? '30%' : '0%',
                    },
                    flexShrink: 0,
                    flexGrow: 0,
                    transition: theme.transitions.create(['flex-basis', 'padding'], {
                        duration: 200,
                    }),
                    pl: {
                        xs: 2,
                        xl: 0,
                    },
                    pr: selectedId ? 2 : 0,
                    pt: {
                        xs: 3,
                        xl: 0,
                    },
                    borderTop: {
                        xs: `1px solid ${theme.palette.customGrey.divider}`,
                        xl: '0px',
                    },
                })}>
                    {selectedId &&
                        <Box sx={{
                            pb: {
                                xs: 3,
                                xl: 0,
                            },
                        }}>
                            <Fade in={!!selectedId} style={{ transitionDelay: selectedId ? '200ms' : '0ms' }}>
                                <Box position="relative" py={2} key={selectedEntry.id}>
                                    <Typography variant="h4" component="h2" mb={2}>
                                        {selectedEntry.eoko_customer?.name}
                                    </Typography>
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        borderRadius: '1rem',
                                    }}>
                                        <IconButton
                                            onClick={() => setSelectedId(null)}
                                            color="terciary"
                                        >
                                            <CloseRoundedIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Fade>
                        </Box>
                    }
                </Box>
            </Box>
        </Box>
    )
}

export default CollectionCompanyManagementList
